import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import useWalletStore from '../../store/useWalletStore';
import { formatNumber, getPriceChangeColor } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import WalletTransaction from './WalletTransaction';
import TransactionDetailsModal from './TransactionDetailsModal';
import TransactionHistory from '../swap/TransactionHistory';
import Btc1TransactionHistory from '../swap/Btc1TransactionHistory';

const WalletPageComponent = () => {
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('spot');
  const [searchTerm, setSearchTerm] = useState('');
  const [hideSmallBalances, setHideSmallBalances] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showBtc1HistoryModal, setShowBtc1HistoryModal] = useState(false);

  const {
    balances,
    transactions,
    fetchBalances,
    fetchWallets,
    fetchTransactions,
    isLoading
  } = useWalletStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchBalances(),
          fetchWallets(),
          fetchTransactions()
        ]);
      } catch (error) {
        console.error('Error fetching wallet data:', error);
      }
    };

    fetchData();
  }, [fetchBalances, fetchWallets, fetchTransactions]);


  // Convert balances object from API to array format for display
  const balancesArray = Object.values(balances || {}).map(balance => ({
    ...balance,
    usdValue: balance.usdValue || 0,
    change24h: balance.change24h || 0
  }));

  // Beautiful color palette for crypto initials with more vibrant colors (similar to market page)
  const getCryptoColors = (symbol) => {
    const colors = [
      { bg: 'bg-gradient-to-br from-amber-400 via-orange-500 to-red-500', text: 'text-white', shadow: 'shadow-amber-500/30', border: 'border-amber-400/20' },
      { bg: 'bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-500', text: 'text-white', shadow: 'shadow-emerald-500/30', border: 'border-emerald-400/20' },
      { bg: 'bg-gradient-to-br from-violet-400 via-purple-500 to-fuchsia-500', text: 'text-white', shadow: 'shadow-violet-500/30', border: 'border-violet-400/20' },
      { bg: 'bg-gradient-to-br from-rose-400 via-pink-500 to-red-500', text: 'text-white', shadow: 'shadow-rose-500/30', border: 'border-rose-400/20' },
      { bg: 'bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-500', text: 'text-white', shadow: 'shadow-sky-500/30', border: 'border-sky-400/20' },
      { bg: 'bg-gradient-to-br from-lime-400 via-green-500 to-emerald-500', text: 'text-white', shadow: 'shadow-lime-500/30', border: 'border-lime-400/20' },
      { bg: 'bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500', text: 'text-white', shadow: 'shadow-yellow-500/30', border: 'border-[#2A356D]/20' },
      { bg: 'bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500', text: 'text-white', shadow: 'shadow-indigo-500/30', border: 'border-indigo-400/20' },
      { bg: 'bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-500', text: 'text-white', shadow: 'shadow-cyan-500/30', border: 'border-cyan-400/20' },
      { bg: 'bg-gradient-to-br from-pink-400 via-rose-500 to-red-500', text: 'text-white', shadow: 'shadow-pink-500/30', border: 'border-pink-400/20' },
    ];
    
    // Use symbol hash to get consistent color
    const hash = symbol.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Get logo path for cryptocurrency symbols
  const getLogoPath = (symbol) => {
    const logoMap = {
      'BTC': '/btc-logo.svg',
      'BTC1': '/btc1-logo.svg',
      'ETH': '/eth-logo.svg',
      'TRX': '/trx-logo.svg',
      'FTT': '/ftt-logo.svg',
      'USDT': '/usdt.svg',
      'USDC': '/usdc.svg',
      'MATIC': '/matic.svg',
      'BNB': '/bnb.svg',
      "OPTMISIM": "/optimism.svg"
    };
    return logoMap[symbol] || null;
  };

  // Process real transactions from API response
  const processedTransactions = (transactions?.results || []).map(transaction => {
    // Map operation_type: 1 = deposit, 2 = withdrawal (based on your API response)
    const operationType = transaction.operation_type === 1 ? 'deposit' : 'withdrawal';

    // Map state: 2 = completed, other states can be mapped as needed
    const status = transaction.state === 2 ? 'completed' : 'pending';

    // Convert timestamp from milliseconds to Date object
    const timestamp = new Date(transaction.created);

    return {
      id: transaction.id,
      type: operationType,
      coin: transaction.currency,
      amount: transaction.amount,
      status: status,
      state: transaction.state, // Keep original state for reference
      timestamp: timestamp.toISOString(),
      updated: transaction.updated ? new Date(transaction.updated).toISOString() : null,
      txHash: transaction.txid || transaction.tx_hash || 'N/A', // Use txid field from withdrawal API
      address: transaction.address || transaction.data?.destination || 'N/A',
      confirmed: transaction.confirmed,
      confirmationsCount: transaction.confirmations_count,
      confirmationsRequired: transaction.confirmations_required_count,
      confirmationToken: transaction.confirmation_token,
      paygateId: transaction.paygate_id,
      blockchainCurrency: transaction.blockchain_currency || transaction.data?.blockchain_currency,
      data: transaction.data
    };
  });

   // Filter balances from API based on search and hide small balances option
   const filteredBalances = balancesArray.filter(balance => {
    const matchesSearch = balance.coin.toLowerCase().includes(searchTerm.toLowerCase()) ||
      balance.name.toLowerCase().includes(searchTerm.toLowerCase());
    const meetsMinBalance = !hideSmallBalances || balance.usdValue >= 1;
    return matchesSearch && meetsMinBalance;
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [currentAssetsPage, setCurrentAssetsPage] = useState(1);
  const itemsPerPage = 5;
  const assetsPerPage = 5;

  // Calculate pagination
  const totalPages = Math.ceil(processedTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedTransactions = processedTransactions.slice(startIndex, endIndex);

  // Calculate assets pagination
  const totalAssetsPages = Math.ceil(filteredBalances.length / assetsPerPage);
  const assetsStartIndex = (currentAssetsPage - 1) * assetsPerPage;
  const assetsEndIndex = assetsStartIndex + assetsPerPage;
  const displayedAssets = filteredBalances.slice(assetsStartIndex, assetsEndIndex);

  // Ensure current page doesn't exceed total pages
  useEffect(() => {
    if (currentAssetsPage > totalAssetsPages && totalAssetsPages > 0) {
      setCurrentAssetsPage(1);
    }
  }, [currentAssetsPage, totalAssetsPages]);

  // Calculate total balance from sum of all actual_usd values from API
  const totalBalance = balancesArray.reduce((sum, balance) => sum + balance.usdValue, 0);

  // Calculate 24h change based on actual balance changes
  const total24hChange = balancesArray.reduce((sum, balance) => {
    const balanceChange = (balance.usdValue * balance.change24h) / 100;
    return sum + balanceChange;
  }, 0);

  const balance24hChange = totalBalance > 0 ? (total24hChange / totalBalance) * 100 : 0;

  // Handle transaction click
  const handleTransactionClick = (transaction) => {
    console.log('Opening transaction details for:', transaction.id);
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  // Close transaction modal
  const handleCloseTransactionModal = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

 

  // Reset assets pagination when search changes
  useEffect(() => {
    setCurrentAssetsPage(1);
  }, [searchTerm]);

  // Reset assets pagination when hideSmallBalances changes
  useEffect(() => {
    setCurrentAssetsPage(1);
  }, [hideSmallBalances]);

  const tabs = [
    { id: 'spot', label: 'Spot', count: balancesArray.length }
  ];

  return (
    <div className="min-h-screen text-white mt-8 sm:mt-12 px-2 sm:px-4 lg:px-8">
      <WalletTransaction />
      <div className="py-2 sm:py-4 lg:py-6">
        {/* Portfolio Overview - Data from API */}
        <div className="w-full gap-4 mb-4 sm:mb-6">
          {/* Total Balance - Compact Mobile Design */}
          <div className="bg-[#2A356D] border border-blue-400 rounded-2xl  mt-2 sm:mt-4  relative overflow-hidden py-4 px-10">
          {/* Mobile: Single row layout */}
            <div className="sm:hidden relative flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center shadow-lg shadow-emerald-500/40 border border-emerald-400/30">
                  <span className="text-white text-sm font-bold">💰</span>
                </div>
                <div>
                  <h3 className="text-sm font-bold bg-gradient-to-r from-emerald-400 via-green-300 to-teal-400 bg-clip-text text-transparent">
                    Total Balance
                  </h3>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold text-white">
                  {balanceVisible ? `$${formatNumber(totalBalance, 2)}` : '****'}
                </span>
                <button
                  onClick={() => setShowBtc1HistoryModal(true)}
                  className="flex items-center space-x-1 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white px-2 py-1 rounded-md shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 transform hover:scale-105"
                  title="View  Transaction History"
                >
                  <CurrencyDollarIcon className="h-4 w-4" />
                  <span className="text-xs font-semibold">Transaction History</span>
                </button>
                <button
                  onClick={() => setBalanceVisible(!balanceVisible)}
                  className="text-slate-300 p-1 rounded bg-slate-700/50 border border-slate-600/50 hover:bg-slate-600/70 hover:text-white transition-colors"
                >
                  {balanceVisible ? (
                    <EyeSlashIcon className="h-3 w-3" />
                  ) : (
                    <EyeIcon className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>

            {/* Desktop: Original layout */}
            <div className="hidden sm:block relative">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-2 sm:space-x-3">
                 
                  <div>
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold !text-white">
                      Total Balance
                    </h3>
                    <p className="text-xs text-slate-400 font-medium">USD Value</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <button
                    onClick={() => setShowBtc1HistoryModal(true)}
                    className="flex items-center space-x-2 bg-[#2A356D] border border-[#FDD00F] !text-whitepx-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-3 rounded-full sm:rounded-full   transition-all duration-300 transform hover:scale-105 self-start sm:self-auto"
                    title="View Transaction History"
                  >
                    <CurrencyDollarIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                    <span className="text-xs sm:text-sm lg:text-base font-bold">Portfolio History</span>
                  </button>
                  <button
                    onClick={() => setBalanceVisible(!balanceVisible)}
                    className="text-slate-300 hover:text-white transition-all duration-300 p-1.5 sm:p-2 rounded-full sm:rounded-full bg-slate-700/50 border border-slate-600/50 hover:bg-slate-600/70 hover:border-emerald-500/50 self-start sm:self-auto"
                  >
                    {balanceVisible ? (
                      <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                    ) : (
                      <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                    )}
                  </button>
                </div>
              </div>
              <div className="relative space-y-3">
                {isLoading ? (
                  <div className="flex items-center space-x-3">
                    <LoadingSpinner size="md" />
                    <span className="text-slate-300 text-base sm:text-lg font-medium">Loading...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-baseline space-y-2 sm:space-y-0 sm:space-x-3">
                      <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white drop-shadow-lg">
                        {balanceVisible ? `$${formatNumber(totalBalance, 2)}` : '****'}
                      </span>
                      <span className="text-xs sm:text-sm lg:text-base text-slate-300 font-semibold bg-slate-700/60 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-slate-600/50 self-start sm:self-auto">USD</span>
                    </div>
                    {/* Temporarily commented out - need trading data */}
                    {/* <div className={`text-xs sm:text-sm lg:text-base font-bold ${getPriceChangeColor(balance24hChange / 100)} flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2`}>
                      <span className="bg-slate-700/60 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-slate-600/50 self-start sm:self-auto">
                        {balance24hChange >= 0 ? '+' : ''}
                        {formatNumber(balance24hChange, 2)}%
                      </span>
                      <span className="text-slate-300 font-normal text-xs sm:text-sm">24h change</span>
                    </div> */}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Tabs - All tabs show the same balance data */}
        <div className="relative bg-gradient-to-br from-slate-800/80 via-slate-700/70 to-slate-800/80 rounded-xl sm:rounded-2xl border-2 border-slate-600/50 shadow-2xl backdrop-blur-md overflow-hidden">
          
          {/* Tab Navigation - Same data for all tabs */}
          <div className="relative border-b border-slate-600/50">
            <div className="flex flex-col space-y-4 p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex space-x-2 sm:space-x-3 overflow-x-auto">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 sm:px-5 lg:px-7 py-2 sm:py-3 text-xs sm:text-sm lg:text-base font-bold rounded-full sm:rounded-full whitespace-nowrap ${activeTab === tab.id
                        ? 'bg-[#0E1696] !text-white  border-2 border-black '
                        : 'text-slate-300 border-2 border-transparent'
                        }`}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full font-semibold ${activeTab === tab.id 
                          ? 'bg-[#111827] !text-white' 
                          : 'bg-slate-600/50 text-slate-300'}`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Search and Filters - Applied across all tabs */}
                <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-none">
                    <MagnifyingGlassIcon className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-3 sm:pl-4 pr-10 sm:pr-12 py-2 sm:py-3 bg-slate-700/50 border-2 !border-[#2A356D] rounded-lg sm:rounded-xl text-white placeholder-slate-400  focus:ring-2 focus:border-[#2A356D] text-xs sm:text-sm w-full sm:w-64 lg:w-72"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assets Table - Responsive design */}
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <table className="w-full hidden lg:table">
              <thead className="bg-[#0E1696] border-b border-black">
                <tr>
                  <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-3 text-left text-xs sm:text-sm font-bold !text-white uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-3 text-center text-xs sm:text-sm font-bold !text-white uppercase tracking-wider">
                    Total Balance
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-3 text-center text-xs sm:text-sm font-bold !text-white uppercase tracking-wider">
                    Available
                  </th>
                  <th className="px-3 sm:px-4 lg:px-6 py-3 sm:py-3 text-center text-xs sm:text-sm font-bold !text-white uppercase tracking-wider">
                    In Order
                  </th>
                 
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-600/30">
                {isLoading ? (
                  // Show loading skeleton rows
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`loading-${index}`} className="animate-pulse">
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-700/50 rounded-full mr-3 sm:mr-4"></div>
                          <div>
                            <div className="h-4 sm:h-5 bg-slate-700/50 rounded w-16 sm:w-20 mb-2"></div>
                            <div className="h-3 sm:h-4 bg-slate-700/50 rounded w-20 sm:w-28"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-center">
                        <div className="h-4 sm:h-5 bg-slate-700/50 rounded w-20 sm:w-24 mx-auto"></div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-center">
                        <div className="h-4 sm:h-5 bg-slate-700/50 rounded w-16 sm:w-20 mx-auto"></div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-center">
                        <div className="h-4 sm:h-5 bg-slate-700/50 rounded w-12 sm:w-16 mx-auto"></div>
                      </td>
                    </tr>
                  ))
                ) : (
                  displayedAssets.map((balance) => {
                    return (
                    <tr key={balance.coin} className="border-l-4 border-transparent">
                      <td className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {getLogoPath(balance.coin) ? (
                            <div className={`w-8 h-8  sm:rounded-full  flex items-center justify-center relative overflow-hidden`}>
                              <img src={getLogoPath(balance.coin)} alt={balance.coin} className="w-full h-full object-contain" />
                            </div>
                          ) : (
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-full shadow-full  flex items-center justify-center ${getCryptoColors(balance.coin).bg}`}>
                              <span className={`text-xs sm:text-sm lg:text-base font-bold rounded-full ${getCryptoColors(balance.coin).text}`}>
                                {balance.coin.slice(0, 2)}
                              </span>
                            </div>
                          )}
                          <div className="ml-2 sm:ml-3 lg:ml-4">
                            <div className="text-xs sm:text-sm lg:text-base font-bold !text-white">{balance.coin}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 whitespace-nowrap text-center">
                        <div className="text-xs sm:text-sm lg:text-base font-semibold !text-white">
                          {balanceVisible ? formatNumber(balance.total, 2) : '****'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 whitespace-nowrap text-center">
                        <div className="text-xs sm:text-sm lg:text-base !text-white">
                          {balanceVisible ? formatNumber(balance.available, 2) : '****'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 whitespace-nowrap text-center">
                        <div className="text-xs sm:text-sm lg:text-base !text-white">
                          {balanceVisible ? formatNumber(balance.locked, 2) : '****'}
                        </div>
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="lg:hidden">
              {isLoading ? (
                // Mobile loading skeleton
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={`mobile-loading-${index}`} className="p-6 border-b border-slate-600/30 animate-pulse">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-slate-700/50 rounded-full mr-4"></div>
                        <div>
                          <div className="h-5 bg-slate-700/50 rounded w-20 mb-2"></div>
                          <div className="h-4 bg-slate-700/50 rounded w-28"></div>
                        </div>
                      </div>
                      <div className="h-5 bg-slate-700/50 rounded w-24"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="h-4 bg-slate-700/50 rounded"></div>
                      <div className="h-4 bg-slate-700/50 rounded"></div>
                      <div className="h-4 bg-slate-700/50 rounded"></div>
                    </div>
                  </div>
                ))
              ) : (
                displayedAssets.map((balance) => {
                  return (
                  <div key={balance.coin} className="p-3 sm:p-4 border-b border-slate-600/30 border-l-4 border-transparent">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div className="flex items-center">
                        <div className="relative">
                          {getLogoPath(balance.coin) ? (
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl shadow-lg  flex items-center justify-center relative overflow-hidden`}>
                              {/* Sparkle effect */}
                              <img src={getLogoPath(balance.coin)} alt={balance.coin} className="w-full h-full object-contain" />
                           
                            </div>
                          ) : (
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center ${getCryptoColors(balance.coin).bg}`}>
                              <span className={`text-sm sm:text-base font-bold ${getCryptoColors(balance.coin).text}`}>
                                {balance.coin.slice(0, 2)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <div className="text-sm sm:text-base font-bold text-white">{balance.coin}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm sm:text-base font-semibold text-white">
                          {balanceVisible ? `$${formatNumber(balance.usdValue, 2)}` : '****'}
                        </div>
                        {/* Temporarily commented out - need trading data */}
                        {/* <div className={`text-xs sm:text-sm ${getPriceChangeColor(balance.change24h / 100)}`}>
                          {balance.change24h >= 0 ? '+' : ''}
                          {balance.change24h.toFixed(2)}%
                        </div> */}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div>
                        <div className="text-slate-400 mb-1">Total</div>
                        <div className="text-white font-semibold">
                          {balanceVisible ? formatNumber(balance.total, 2) : '****'}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400 mb-1">Available</div>
                        <div className="text-slate-300">
                          {balanceVisible ? formatNumber(balance.available, 2) : '****'}
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400 mb-1">In Order</div>
                        <div className="text-slate-300">
                          {balanceVisible ? formatNumber(balance.locked, 2) : '****'}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>

            {!isLoading && filteredBalances.length === 0 && (
              <div className="text-center py-12 sm:py-16">
                <CurrencyDollarIcon className="h-12 w-12 sm:h-16 sm:w-16 text-slate-500 mx-auto mb-4 sm:mb-6" />
                <div className="text-white text-lg sm:text-xl font-semibold mb-2">No assets found</div>
                <div className="text-slate-400 text-sm sm:text-base">
                  {searchTerm ? 'Try adjusting your search terms' : 'Start by depositing some crypto'}
                </div>
              </div>
            )}

            {/* Assets Pagination */}
            {!isLoading && filteredBalances.length > 0 && totalAssetsPages > 1 && (
              <div className="p-3 sm:p-4 lg:p-6 border-t border-slate-600/50">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                  <div className="text-xs sm:text-sm text-white font-medium text-center sm:text-left">
                    Showing <span className="text-white font-bold">{assetsStartIndex + 1}</span> to <span className="text-white font-bold">{Math.min(assetsEndIndex, filteredBalances.length)}</span> of <span className="text-white font-bold">{filteredBalances.length}</span> assets
                  </div>
                  
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <button
                      onClick={() => setCurrentAssetsPage(Math.max(1, currentAssetsPage - 1))}
                      disabled={currentAssetsPage === 1}
                      className="flex items-center px-2 sm:px-3 py-1.5 text-xs font-semibold !text-white bg-slate-700/60 border-2 border-slate-600/60 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Prev</span>
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(window.innerWidth < 640 ? 2 : 3, totalAssetsPages) }, (_, i) => {
                        let pageNum;
                        if (totalAssetsPages <= (window.innerWidth < 640 ? 2 : 3)) {
                          pageNum = i + 1;
                        } else if (currentAssetsPage <= 2) {
                          pageNum = i + 1;
                        } else if (currentAssetsPage >= totalAssetsPages - 1) {
                          pageNum = totalAssetsPages - (window.innerWidth < 640 ? 1 : 2) + i;
                        } else {
                          pageNum = currentAssetsPage - 1 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentAssetsPage(pageNum)}
                            className={`px-2 sm:px-3 py-1.5 text-xs font-bold rounded-lg ${
                              currentAssetsPage === pageNum
                                ? 'bg-[#0E1696] !text-white shadow-lg border-2 border-black'
                                : '!text-white bg-slate-700/60 border-2 border-slate-600/60'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentAssetsPage(Math.min(totalAssetsPages, currentAssetsPage + 1))}
                      disabled={currentAssetsPage === totalAssetsPages}
                      className="flex items-center px-2 sm:px-3 py-1.5 text-xs font-semibold !text-white bg-slate-700/60 border-2 border-slate-600/60 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRightIcon className="w-3 h-3 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Transactions - Real data from API */}
        <div className="mt-6 sm:mt-8 lg:mt-10 relative bg-gradient-to-br from-slate-800/80 via-slate-700/70 to-slate-800/80 rounded-xl sm:rounded-2xl border-2 border-slate-600/50 shadow-2xl backdrop-blur-md">
          
          <div className="relative p-3 sm:p-4 lg:p-6 border-b border-slate-600/50">
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold !text-white">
                 Recent Transactions
              </h3>
             
            </div>
          </div>

          <div className="divide-y divide-slate-600/30">
            {/* Display real transactions from API */}
            {displayedTransactions.map((transaction) => (
              <div 
                key={transaction.id} 
                className="p-3 sm:p-4 lg:p-6 border-l-4 border-transparent rounded-lg mx-1 sm:mx-2 my-1"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                  <div className="flex items-center space-x-4 sm:space-x-5">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2 ${transaction.type === 'deposit' ? 'bg-emerald-900/30 border-emerald-500/30' : 'bg-blue-900/30 border-blue-500/30'
                      }`}>
                      {transaction.type === 'deposit' ? (
                        <ArrowDownIcon className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-400" />
                      ) : (
                        <ArrowUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-base sm:text-lg font-semibold text-white">
                        {transaction.type === 'deposit' ? 'Deposit' : 'Withdrawal'} {transaction.coin}
                      </div>
                      <div className="text-sm sm:text-base text-slate-400 mt-1">
                        ID: {transaction.id} | Paygate: {transaction.paygateId || 'N/A'}
                        {transaction.txHash && transaction.txHash !== 'N/A' && (
                          <span className="ml-2 text-white font-medium">• TX: {transaction.txHash.substring(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-left sm:text-right">
                    {/* Display transaction amount from API data */}
                    <div className="text-base sm:text-lg font-bold text-white">
                      {transaction.type === 'deposit' ? '+' : '-'}
                      {formatNumber(transaction.amount, 2)} {transaction.coin}
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-2 sm:mt-0 sm:justify-end">
                      {/* Display transaction status mapped from API state */}
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${transaction.status === 'completed'
                        ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-500/30'
                        : transaction.status === 'pending'
                          ? 'bg-yellow-900/40 text-white border border-[#2A356D]/30'
                          : 'bg-red-900/40 text-red-300 border border-red-500/30'
                        }`}>
                        {transaction.status === 'completed' && <CheckCircleIcon className="h-4 w-4 mr-1.5" />}
                        {transaction.status === 'pending' && <ClockIcon className="h-4 w-4 mr-1.5" />}
                        {transaction.status === 'failed' && <XCircleIcon className="h-4 w-4 mr-1.5" />}
                        {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                      </div>
                      
                      {/* View Details Button */}
                      <button
                        onClick={() => {
                          console.log('View Details button clicked for transaction:', transaction.id);
                          handleTransactionClick(transaction);
                        }}
                        style={{
                          backgroundColor: '#2563eb',
                          color: 'white',
                          padding: '8px 16px',
                          border: '1px solid #3b82f6',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          pointerEvents: 'auto',
                          zIndex: 10,
                          position: 'relative'
                        }}
                      >
                        <EyeIcon className="w-4 h-4 mr-1" /> View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Pagination */}
          <div className="relative p-3 sm:p-4 lg:p-6 border-t border-slate-600/50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
              <div className="text-xs sm:text-sm !text-white font-medium text-center sm:text-left">
                Showing <span className="text-white font-bold">{startIndex + 1}</span> to <span className="text-white font-bold">{Math.min(endIndex, processedTransactions.length)}</span> of <span className="text-white font-bold">{processedTransactions.length}</span> transactions
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-blue-300 bg-slate-700/60 border-2 border-slate-600/60 rounded-xl hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-md hover:shadow-emerald-500/20"
                >
                  <ChevronLeftIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>
                
                {/* Page numbers */}
                <div className="flex space-x-2">
                  {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage <= 2) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 1) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = currentPage - 1 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold rounded-xl transition-all duration-300 ${
                          currentPage === pageNum
                            ? 'bg-[#0E1696] !text-white shadow-lg border-2 border-black'
                            : '!text-white bg-slate-700/60 border-2 border-slate-600/60 hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 hover:shadow-md hover:shadow-emerald-500/20'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-whitebg-slate-700/60 border-2 border-slate-600/60 rounded-xl hover:bg-slate-600/80 hover:text-white hover:border-emerald-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-md hover:shadow-emerald-500/20"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={showTransactionModal}
        onClose={handleCloseTransactionModal}
        transaction={selectedTransaction}
      />

      {/* BTC1 Transaction History Modal */}
      {showBtc1HistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 rounded-2xl border-2 border-slate-600/50 shadow-2xl backdrop-blur-md w-full max-w-4xl max-h-[90vh] overflow-hidden">
            
            {/* Header */}
            <div className="relative p-6 border-b border-slate-600/50 bg-blue-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-300 rounded-xl flex items-center justify-center shadow-lg">
                    <CurrencyDollarIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white"> Transaction History</h3>
                    <p className="text-sm text-emerald-200">Complete transaction history</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBtc1HistoryModal(false)}
                  className="text-slate-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-700/50"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <Btc1TransactionHistory />
            </div>
          </div>
        </div>
      )}


      {/* Transaction History Component from Trade BTC1 page */}
        {/* <div className="mt-6 sm:mt-8 lg:mt-10">
          <TransactionHistory 
            type="all" 
            limit={10} 
            showTitle={true} 
          />
        </div> */}


        <div className="mt-6 sm:mt-8 lg:mt-10">
          <Btc1TransactionHistory />
        </div>
      </div>

  );
};

export default WalletPageComponent; 